import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  Stack,
  Paper,
} from '@mui/material';
import videoCreatorApi, { getVideoCreatorBaseUrl } from '../../utils/videoCreatorApi';
import api from '../../utils/api';

function mediaUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  // /api/video-creator/... → absolute via same origin
  if (path.startsWith('/api/')) return path;
  return `${getVideoCreatorBaseUrl().replace(/\/api\/video-creator$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Play a generated help video with voice selection + transcript text.
 * Credits watch minutes to /api/help/video-watch on close.
 */
export default function HelpVideoPlayerDialog({ open, onClose, video, moduleId, moduleTitle }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const watchedSecRef = useRef(0);
  const lastTickRef = useRef(null);
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState('en-us');
  const [transcript, setTranscript] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loadingVoice, setLoadingVoice] = useState(false);
  const [err, setErr] = useState('');

  const version = video?.version;
  const videoSrc = mediaUrl(video?.url);

  useEffect(() => {
    if (!open) return;
    watchedSecRef.current = 0;
    lastTickRef.current = null;
    setErr('');
    setAudioUrl(null);
    setVoiceId(video?.default_voice || 'en-us');
    videoCreatorApi
      .get('/v1/voices')
      .then((r) => setVoices(r.data?.data || []))
      .catch(() => setVoices([]));
    if (moduleId && version) {
      videoCreatorApi
        .get(`/v1/modules/${moduleId}/versions/${version}/transcript`)
        .then((r) => setTranscript(r.data?.data || null))
        .catch(() => setTranscript(null));
    }
  }, [open, moduleId, version, video?.default_voice]);

  useEffect(() => {
    if (!open || !moduleId || !version) return undefined;
    let cancelled = false;
    const applyVoice = async () => {
      setErr('');
      setLoadingVoice(true);
      try {
        // Always use clear neural narration for the selected voice (cached after first prepare).
        const r = await videoCreatorApi.post(
          `/v1/modules/${moduleId}/versions/${version}/audio`,
          { voice: voiceId },
          { timeout: 300000 }
        );
        if (cancelled) return;
        const data = r.data?.data || {};
        if (data.use_video_audio || !data.url) {
          setAudioUrl(null);
          const v = videoRef.current;
          if (v) v.muted = false;
          return;
        }
        const url = mediaUrl(data.url);
        setAudioUrl(url);
        const v = videoRef.current;
        if (v) {
          v.muted = true;
        }
      } catch (e) {
        if (!cancelled) {
          setErr(
            e.response?.data?.message ||
              e.message ||
              'Could not prepare this voice. Check network access from video-creator, then try again.'
          );
          setAudioUrl(null);
          const v = videoRef.current;
          if (v) v.muted = false;
        }
      } finally {
        if (!cancelled) setLoadingVoice(false);
      }
    };
    applyVoice();
    return () => {
      cancelled = true;
    };
  }, [open, voiceId, moduleId, version]);

  useEffect(() => {
    if (!audioUrl || !audioRef.current || !videoRef.current) return;
    const a = audioRef.current;
    const v = videoRef.current;
    const syncPlay = () => {
      a.currentTime = v.currentTime;
      a.play().catch(() => null);
    };
    const syncPause = () => a.pause();
    const onSeek = () => {
      a.currentTime = v.currentTime;
    };
    v.addEventListener('play', syncPlay);
    v.addEventListener('pause', syncPause);
    v.addEventListener('seeked', onSeek);
    return () => {
      v.removeEventListener('play', syncPlay);
      v.removeEventListener('pause', syncPause);
      v.removeEventListener('seeked', onSeek);
    };
  }, [audioUrl]);

  useEffect(() => {
    if (!open) return undefined;
    const iv = setInterval(() => {
      const v = videoRef.current;
      if (v && !v.paused && !v.ended) {
        watchedSecRef.current += 2;
      }
    }, 2000);
    return () => clearInterval(iv);
  }, [open]);

  const creditAndClose = async () => {
    const minutes = Math.max(0, Math.round(watchedSecRef.current / 60));
    if (minutes >= 1 && moduleId) {
      try {
        await api.post('/help/video-watch', {
          module_id: moduleId,
          version,
          minutes,
          title: video?.title || moduleTitle || moduleId,
        });
      } catch {
        /* non-blocking */
      }
    }
    onClose?.({ minutes });
  };

  return (
    <Dialog open={open} onClose={creditAndClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {video?.title || moduleTitle || 'Help video'}
        {version ? ` · ${version}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <FormControl size="small" sx={{ maxWidth: 360 }}>
            <InputLabel id="help-voice-label">Audio voice</InputLabel>
            <Select
              labelId="help-voice-label"
              label="Audio voice"
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              disabled={loadingVoice}
            >
              {(voices.length ? voices : [{ id: 'en-us', label: 'US English (default)' }]).map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.label}
                  {v.is_default ? ' ★' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            Voices: US, British, or Indian English. Clear neural speech is prepared on play (first time may take a
            minute) and cached — the video is not re-recorded.
          </Typography>
          {loadingVoice && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="caption">Preparing clear voice (first time only)…</Typography>
            </Stack>
          )}
          {err && (
            <Typography color="error" variant="body2">
              {err}
            </Typography>
          )}
          <Box sx={{ bgcolor: '#000', borderRadius: 1, overflow: 'hidden' }}>
            <video
              ref={videoRef}
              key={videoSrc}
              src={videoSrc}
              controls
              playsInline
              style={{ width: '100%', maxHeight: 420, display: 'block' }}
            />
            {audioUrl ? <audio ref={audioRef} src={audioUrl} preload="auto" /> : null}
          </Box>
          <Paper variant="outlined" sx={{ p: 2, maxHeight: 220, overflow: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom>
              Narration text
            </Typography>
            {transcript?.segments?.length ? (
              transcript.segments.map((s) => (
                <Box key={s.n || s.text} sx={{ mb: 1.5 }}>
                  {s.caption ? (
                    <Typography variant="caption" color="primary" display="block" fontWeight={700}>
                      {s.caption}
                    </Typography>
                  ) : null}
                  <Typography variant="body2" color="text.secondary">
                    {s.text}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                {transcript?.full_text || 'Transcript will appear after a narrated generate completes.'}
              </Typography>
            )}
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={creditAndClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
